import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getAllEvents, deleteEvent } from "../../../../services/eventServices";
import { useNotification } from "../../../../components/Notification/NotificationManager";

import {
  DEFAULT_FILTERS,
  FIXED_EVENT_CATEGORIES,
  buildEventStats,
  extractEvents,
  filterEvents,
  getUniqueLevels,
  getUniqueTypes,
  paginateItems,
  sortEvents,
} from "../utils/adminEventsUtils";

export function useAdminEvents() {
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);
  const selectedEventRef = useRef(null);

  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  useEffect(() => {
    selectedEventRef.current = selectedEvent;
  }, [selectedEvent]);

  const fetchEvents = useCallback(async ({ silent = false } = {}) => {
    const isInitialLoad = !hasLoadedOnceRef.current;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const data = await getAllEvents();
      const safeEvents = extractEvents(data);

      setEvents(safeEvents);

      if (!silent && !isInitialLoad) {
        notificationRef.current?.("Événements actualisés.", "success");
      }
    } catch (error) {
      console.error("Erreur récupération événements :", error);

      notificationRef.current?.(
        "Erreur lors de la récupération des événements.",
        "error",
      );
    } finally {
      hasLoadedOnceRef.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents({ silent: true });
  }, [fetchEvents]);

  const filteredEvents = useMemo(() => {
    return sortEvents(filterEvents(events, filters), filters.sort);
  }, [events, filters]);

  const pagination = useMemo(() => {
    return paginateItems(filteredEvents, page, filters.perPage);
  }, [filteredEvents, filters.perPage, page]);

  const stats = useMemo(() => {
    return buildEventStats(events);
  }, [events]);

  const typeOptions = useMemo(() => {
    return getUniqueTypes(events);
  }, [events]);

  const levelOptions = useMemo(() => {
    return getUniqueLevels(events);
  }, [events]);

  const categoryOptions = FIXED_EVENT_CATEGORIES;

  const updateFilter = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));

    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  }, []);

  const openEventDrawer = useCallback((event) => {
    setSelectedEvent(event);
    setIsDrawerOpen(true);
  }, []);

  const closeEventDrawer = useCallback(() => {
    setSelectedEvent(null);
    setIsDrawerOpen(false);
  }, []);

  const handleDeleteEvent = useCallback(async (eventId) => {
    const confirmed = window.confirm(
      "Êtes-vous sûr de vouloir supprimer cet événement ?",
    );

    if (!confirmed) return false;

    try {
      await deleteEvent(eventId);

      setEvents((prevEvents) =>
        prevEvents.filter((event) => String(event.id) !== String(eventId)),
      );

      if (String(selectedEventRef.current?.id) === String(eventId)) {
        setSelectedEvent(null);
        setIsDrawerOpen(false);
      }

      notificationRef.current?.("Événement supprimé avec succès.", "success");

      return true;
    } catch (error) {
      console.error("Erreur suppression événement :", error);

      notificationRef.current?.(
        "Une erreur est survenue pendant la suppression.",
        "error",
      );

      return false;
    }
  }, []);

  return {
    events,
    filteredEvents,
    paginatedEvents: pagination.items,
    filters,
    stats,
    typeOptions,
    levelOptions,
    categoryOptions,
    selectedEvent,
    isDrawerOpen,
    loading,
    refreshing,
    page: pagination.page,
    totalPages: pagination.totalPages,
    fetchEvents,
    updateFilter,
    resetFilters,
    setPage,
    openEventDrawer,
    closeEventDrawer,
    handleDeleteEvent,
  };
}